import * as React from 'react';
import './HomePage.css';

import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router';

import { signIn } from '../App/actions';

import Logo from '../../assets/Logo.png';

import { Controller, useForm } from 'react-hook-form';
import Input from '../../components/Input/Input';
import Button from '../../components/Button/Button';
import { Link } from 'react-router-dom';
import Text from '../../components/Text/Text';
import FormItem from '../../components/FormItem/FormItem';
import Flex from '../../components/Flex/Flex';
import { useTranslation } from 'react-i18next';
import {
  CompressOutlined,
  CrownOutlined,
  LineChartOutlined,
  ThunderboltOutlined,
  UserOutlined,
} from '@ant-design/icons';

type FormValues = {
  email: string;
  password: string;
};

const HomePage = React.memo(() => {
  const { t } = useTranslation('translation');
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const {
    handleSubmit,
    control,
    formState: { errors },
  } = useForm<FormValues>({});

  const onSubmit = handleSubmit(({ email, password }) => {
    dispatch(
      signIn({
        email,
        password,
      }),
    );
  });

  const handleSignUpClick = React.useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      navigate('/register');
    },
    [navigate],
  );

  const features = [
    {
      icon: <LineChartOutlined />,
      text: t('home_feature_stats'),
    },
    {
      icon: <UserOutlined />,
      text: t('home_feature_profiles'),
    },
    {
      icon: <ThunderboltOutlined />,
      text: t('home_feature_autopaste'),
    },
  ];

  const portablePoints = [
    t('home_portable_point1'),
    t('home_portable_point2'),
    t('home_portable_point3'),
  ];

  return (
    <div className="AuthPage HomePage">
      <div className="HomePage__glow HomePage__glow--left" aria-hidden />
      <div className="HomePage__glow HomePage__glow--right" aria-hidden />

      <div className="HomePage__shell">
        <section className="HomePage__Hero">
          <span className="PageHero__eyebrow HomePage__eyebrow">
            <CrownOutlined /> Fish Hunter
          </span>
          <div className="HomePage__TitleBlock">
            <Text size={6} bold className="HomePage__TitleLine">
              {t('home_hero_title_line1')}
            </Text>
            <Text size={5} bold className="HomePage__TitleLine HomePage__TitleLine--accent">
              {t('home_hero_title_line2')}
            </Text>
          </div>
          <Text size={3} className="HomePage__Subtitle">
            {t('home_hero_subtitle')}
          </Text>

          <Flex vertical gap={12} className="HomePage__Features">
            {features.map((feature) => (
              <Flex key={feature.text} align="center" gap={12} className="HomePage__Feature">
                <span className="HomePage__FeatureIcon">{feature.icon}</span>
                <Text size={3} className="HomePage__FeatureText">
                  {feature.text}
                </Text>
              </Flex>
            ))}
          </Flex>

          <Flex gap={8} wrap className="HomePage__Rooms">
            {['PS', '888', 'WPN', 'CHI', 'CPR', 'IP'].map((room) => (
              <span key={room} className="HomePage__RoomChip">
                {room}
              </span>
            ))}
          </Flex>
        </section>

        <div className="AuthCard HomePage__Card">
          <img className="AuthCard__logo HomePage__CardLogo" src={Logo} alt="Fish Hunter" />
          <Text className="AuthCard__title" size={5} bold>
            {t('sign_in')}
          </Text>
          <Text type="secondary" size={2} className="HomePage__CardHint">
            {t('home_card_hint')}
          </Text>
          <form className="AuthCard__form" onSubmit={onSubmit}>
            <FormItem error={errors.email?.message}>
              <Controller
                name="email"
                control={control}
                rules={{
                  required: t('required'),
                  pattern: {
                    value: /\S+@\S+\.\S+/,
                    message: t('entered_value_does_not_match_email_format'),
                  },
                }}
                render={({ field }) => (
                  <Input
                    placeholder="Email"
                    autoComplete="off"
                    aria-invalid={errors.email?.message ? true : false}
                    {...field}
                  />
                )}
              />
            </FormItem>
            <FormItem error={errors.password?.message}>
              <Controller
                name="password"
                control={control}
                rules={{ required: t('required') }}
                render={({ field }) => (
                  <Input
                    placeholder={t('password')}
                    autoComplete="off"
                    type="password"
                    aria-invalid={errors.password?.message ? true : false}
                    {...field}
                  />
                )}
              />
            </FormItem>

            <FormItem className="HomePage__ForgotPassword">
              <Link to="/reset">{t('forgot_your_password')}</Link>
            </FormItem>

            <FormItem>
              <Button className="AuthCard__action" type="primary">
                {t('sign_in')}
              </Button>
            </FormItem>
            <FormItem>
              <Button className="AuthCard__action" onClick={handleSignUpClick}>
                {t('sign_up')}
              </Button>
            </FormItem>
          </form>

          <div className="HomePage__CardLinks">
            <Link className="HomePage__CardLink" to="/subscription">
              {t('subscription')}
            </Link>
            <span className="HomePage__CardLinksDivider">·</span>
            <Link className="HomePage__CardLink" to="/faq">
              FAQ
            </Link>
          </div>
        </div>

        <section className="HomePage__Portable">
          <Flex vertical gap={12} className="HomePage__PortableHead">
            <span className="HomePage__PortableIcon">
              <CompressOutlined />
            </span>
            <div>
              <Text size={4} bold className="HomePage__PortableTitle">
                {t('home_portable_title')}
              </Text>
              <Text size={2} className="HomePage__PortableSubtitle">
                {t('home_portable_subtitle')}
              </Text>
            </div>
          </Flex>

          <Flex vertical gap={10} className="HomePage__PortablePoints">
            {portablePoints.map((point) => (
              <Text key={point} size={3} className="HomePage__PortablePoint">
                {point}
              </Text>
            ))}
          </Flex>

          <Link className="HomePage__PortableLink" to="/faq">
            {t('home_portable_link')}
          </Link>
        </section>
      </div>
    </div>
  );
});

export default HomePage;
